// pages/PresupuestoDetallado.jsx

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';
import '../styles/PresupuestoDetallado.css';
import { useFiscal } from '../hooks/useFiscal';

const PresupuestoDetallado = () => {
  const fiscal = useFiscal();
  const { id: proyectoId } = useParams();
  const [partidas, setPartidas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [editando, setEditando] = useState(null);
  const [nuevaPartida, setNuevaPartida] = useState({
    codigo: '',
    descripcion: '',
    unidad: 'glb',
    cantidad: 1,
    precio_unitario: 0
  });
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  
  // Estados para el catálogo
  const [usarCatalogo, setUsarCatalogo] = useState(true);
  const [categoriasPartida, setCategoriasPartida] = useState([]);
  const [partidasEstandar, setPartidasEstandar] = useState([]);
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState('');
  const [partidaSeleccionada, setPartidaSeleccionada] = useState('');

  useEffect(() => {
    cargarPartidas();
    cargarCatalogo();
  }, [proyectoId]);

  const cargarCatalogo = async () => {
    try {
      const [resCategorias, resPartidas] = await Promise.all([
        api.get('/categorias-partida'),
        api.get('/partidas-estandar')
      ]);
      setCategoriasPartida(resCategorias.data);
      setPartidasEstandar(resPartidas.data);
    } catch (error) {
      console.error('Error cargando catálogo:', error);
    }
  };

  const cargarPartidas = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/proyectos/${proyectoId}/partidas`);
      setPartidas(response.data.partidas || []);
    } catch (error) {
      console.error('Error cargando partidas:', error);
      alert('Error al cargar partidas');
    } finally {
      setLoading(false);
    }
  };

  const handleNuevaPartidaChange = (e) => {
    const { name, value } = e.target;
    setNuevaPartida(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const seleccionarPartidaCatalogo = (e) => {
    const partidaId = e.target.value;
    setPartidaSeleccionada(partidaId);
    
    if (partidaId) {
      const partida = partidasEstandar.find(p => p.id === parseInt(partidaId));
      if (partida) {
        setNuevaPartida(prev => ({
          ...prev,
          codigo: partida.codigo || '',
          descripcion: partida.nombre,
          unidad: partida.unidad || 'glb',
          precio_unitario: partida.precio_referencia || 0  // ← AGREGADO
        }));
      }
    }
  };

  const toggleUsarCatalogo = () => {
    setUsarCatalogo(!usarCatalogo);
    setCategoriaSeleccionada('');
    setPartidaSeleccionada('');
    setNuevaPartida({
      codigo: '',
      descripcion: '',
      unidad: 'glb',
      cantidad: 1,
      precio_unitario: 0
    });
  };

  const partidasFiltradas = categoriaSeleccionada
    ? partidasEstandar.filter(p => p.CategoriaPartidaId === parseInt(categoriaSeleccionada))
    : partidasEstandar;

  const agregarPartida = async (e) => {
    e.preventDefault();
    
    if (!nuevaPartida.descripcion.trim()) {
      alert('La descripción es obligatoria');
      return;
    }

    try {
      setGuardando(true);
      await api.post(`/proyectos/${proyectoId}/partidas`, {
        ...nuevaPartida,
        orden: partidas.length
      });
      
      setNuevaPartida({
        codigo: '',
        descripcion: '',
        unidad: 'glb',
        cantidad: 1,
        precio_unitario: 0
      });
      setCategoriaSeleccionada('');
      setPartidaSeleccionada('');
      setMostrarFormulario(false);
      await cargarPartidas();
    } catch (error) {
      console.error('Error agregando partida:', error);
      alert(error.response?.data?.error || 'Error al agregar partida');
    } finally {
      setGuardando(false);
    }
  };

  const iniciarEdicion = (partida) => {
    setEditando({
      id: partida.id,
      codigo: partida.codigo || '',
      descripcion: partida.descripcion,
      unidad: partida.unidad || 'glb',
      cantidad: partida.cantidad,
      precio_unitario: partida.precio_unitario
    });
  };

  const cancelarEdicion = () => {
    setEditando(null);
  };

  const guardarEdicion = async (partidaId) => {
    try {
      setGuardando(true);
      
      // Enviar solo campos editables, sin monto_total ni campos internos
      // El backend calculará monto_total automáticamente
      const { id, monto_total, created_at, updated_at, activo, ProyectoId, ...datosActualizados } = editando;
      
      await api.put(`/proyectos/${proyectoId}/partidas/${partidaId}`, datosActualizados);
      
      setEditando(null);
      await cargarPartidas();
    } catch (error) {
      console.error('Error actualizando partida:', error);
      alert(error.response?.data?.error || 'Error al actualizar partida');
    } finally {
      setGuardando(false);
    }
  };

  const eliminarPartida = async (partidaId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta partida?')) return;

    try {
      await api.delete(`/proyectos/${proyectoId}/partidas/${partidaId}`);
      await cargarPartidas();
    } catch (error) {
      console.error('Error eliminando partida:', error);
      alert('Error al eliminar partida');
    }
  };

  const calcularMonto = (cantidad, precioUnitario, unidad = null, partidaActual = null) => {
    // Si es una partida de porcentaje
    if (unidad === '%' || partidaActual?.unidad === '%') {
      // Calcular subtotal de partidas directas (no porcentaje)
      const partidasDirectas = partidas.filter(p => p.unidad !== '%');
      const subtotalDirecto = partidasDirectas.reduce((sum, p) => {
        const monto = p.monto_total && p.monto_total > 0
          ? parseFloat(p.monto_total)
          : parseFloat(p.cantidad || 0) * parseFloat(p.precio_unitario || 0);
        return sum + monto;
      }, 0);
      
      // Calcular porcentaje sobre subtotal
      const porcentaje = parseFloat(precioUnitario || 0) / 100;
      return subtotalDirecto * porcentaje;
    }
    
    // Partida normal
    return parseFloat(cantidad || 0) * parseFloat(precioUnitario || 0);
  };

  const calcularTotalPresupuesto = () => {
    // Separar partidas directas y de porcentaje
    const partidasDirectas = partidas.filter(p => p.unidad !== '%');
    const partidasPorcentaje = partidas.filter(p => p.unidad === '%');
    
    // Calcular subtotal directo
    const subtotalDirecto = partidasDirectas.reduce((sum, p) => {
      const montoPartida = p.monto_total && p.monto_total > 0 
        ? parseFloat(p.monto_total)
        : parseFloat(p.cantidad || 0) * parseFloat(p.precio_unitario || 0);
      return sum + montoPartida;
    }, 0);
    
    // Calcular partidas de porcentaje sobre el subtotal
    const subtotalGestion = partidasPorcentaje.reduce((sum, p) => {
      const porcentaje = parseFloat(p.precio_unitario || 0) / 100;
      const monto = subtotalDirecto * porcentaje;
      return sum + monto;
    }, 0);
    
    return subtotalDirecto + subtotalGestion;
  };

  const formatearMonto = (monto) => {
    return new Intl.NumberFormat('es-DO', {
      style: 'currency',
      currency: fiscal.moneda?.codigo || 'DOP'
    }).format(monto);
  };

  if (loading) {
    return (
      <div className="presupuesto-container">
        <div className="loading">Cargando presupuesto...</div>
      </div>
    );
  }

  return (
    <div className="presupuesto-container">
      <div className="presupuesto-header">
        <div>
          <h2>📋 Presupuesto Detallado</h2>
          <p className="subtitle">Partidas y costos del proyecto</p>
        </div>
        <button 
          className="btn btn-primary"
          onClick={() => setMostrarFormulario(!mostrarFormulario)}
        >
          {mostrarFormulario ? '✖ Cancelar' : '+ Agregar Partida'}
        </button>
      </div>

      {/* Formulario nueva partida */}
      {mostrarFormulario && (
        <div className="formulario-partida">
          <h3>Nueva Partida</h3>
          
          {/* Toggle para usar catálogo */}
          <div className="toggle-catalogo">
            <label className="toggle-label">
              <input
                type="checkbox"
                checked={usarCatalogo}
                onChange={toggleUsarCatalogo}
              />
              <span>Usar catálogo de partidas estándar</span>
            </label>
          </div>

          <form onSubmit={agregarPartida}>
            {usarCatalogo ? (
              <>
                {/* Selector de catálogo */}
                <div className="form-grid">
                  <div className="form-group">
                    <label>Categoría</label>
                    <select
                      value={categoriaSeleccionada}
                      onChange={(e) => {
                        setCategoriaSeleccionada(e.target.value);
                        setPartidaSeleccionada('');
                      }}
                      className="form-control"
                    >
                      <option value="">Todas las categorías</option>
                      {categoriasPartida.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.nombre}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Partida del catálogo *</label>
                    <select
                      value={partidaSeleccionada}
                      onChange={seleccionarPartidaCatalogo}
                      className="form-control"
                      required
                    >
                      <option value="">Seleccionar partida</option>
                      {partidasFiltradas.map((partida) => (
                        <option key={partida.id} value={partida.id}>
                          {partida.codigo ? `${partida.codigo} - ` : ''}{partida.nombre}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Mostrar descripción seleccionada */}
                {nuevaPartida.descripcion && (
                  <div className="descripcion-seleccionada">
                    <strong>Descripción:</strong> {nuevaPartida.descripcion}
                    {nuevaPartida.codigo && <span> (Código: {nuevaPartida.codigo})</span>}
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Entrada manual */}
                <div className="form-grid">
                  <div className="form-group">
                    <label>Código</label>
                    <input
                      type="text"
                      name="codigo"
                      value={nuevaPartida.codigo}
                      onChange={handleNuevaPartidaChange}
                      placeholder="Ej: P-001"
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label>Descripción *</label>
                    <input
                      type="text"
                      name="descripcion"
                      value={nuevaPartida.descripcion}
                      onChange={handleNuevaPartidaChange}
                      placeholder="Ej: Excavación general"
                      className="form-control"
                      required
                    />
                  </div>
                </div>
              </>
            )}

            {/* Campos comunes (siempre visibles) */}
            <div className="form-grid">
             <div className="form-group">
  <label>Unidad *</label>
  <select
    name="unidad"
    value={nuevaPartida.unidad}
    onChange={handleNuevaPartidaChange}
    className="form-control"
    disabled={usarCatalogo && partidaSeleccionada}
    style={usarCatalogo && partidaSeleccionada ? { background: '#f0f0f0', cursor: 'not-allowed' } : {}}
  >
    <option value="glb">Global</option>
    <option value="m²">m² - Metro cuadrado</option>
    <option value="m³">m³ - Metro cúbico</option>
    <option value="ml">ml - Metro lineal</option>
    <option value="un">un - Unidad</option>
    <option value="kg">kg - Kilogramo</option>
    <option value="ton">ton - Tonelada</option>
    <option value="saco">saco</option>
    <option value="gal">gal - Galón</option>
    <option value="%">% - Porcentaje</option>
  </select>
  {usarCatalogo && partidaSeleccionada && (
    <small style={{ color: '#6c757d', marginTop: '0.25rem', display: 'block' }}>
      ✓ Unidad predefinida del catálogo
    </small>
  )}
</div>

              <div className="form-group">
                <label>Cantidad *</label>
                <input
                  type="number"
                  name="cantidad"
                  value={nuevaPartida.cantidad}
                  onChange={handleNuevaPartidaChange}
                  min="0"
                  step="0.01"
                  className="form-control"
                  required
                />
              </div>

              <div className="form-group">
                <label>Precio Unitario *</label>
                <input
                  type="number"
                  name="precio_unitario"
                  value={nuevaPartida.precio_unitario}
                  onChange={handleNuevaPartidaChange}
                  min="0"
                  step="0.01"
                  className="form-control"
                  required
                />
              </div>

              <div className="form-group">
                <label>Monto Total</label>
                <div className="monto-calculado">
                  {formatearMonto(calcularMonto(
                    nuevaPartida.cantidad, 
                    nuevaPartida.precio_unitario, 
                    nuevaPartida.unidad
                  ))}
                  {nuevaPartida.unidad === '%' && (
                    <small style={{ display: 'block', color: '#666', marginTop: '4px' }}>
                      Calculado sobre subtotal de partidas directas
                    </small>
                  )}
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button 
                type="button" 
                onClick={() => setMostrarFormulario(false)}
                className="btn btn-secondary"
              >
                Cancelar
              </button>
              <button 
                type="submit" 
                className="btn btn-success"
                disabled={guardando}
              >
                {guardando ? 'Guardando...' : '💾 Guardar Partida'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Tabla de partidas */}
      <div className="partidas-tabla-container">
        {partidas.length === 0 ? (
          <div className="empty-state">
            <p className="empty-icon">📋</p>
            <h3>Sin partidas presupuestarias</h3>
            <p>Agrega partidas para detallar el presupuesto del proyecto</p>
            <button 
              className="btn btn-primary"
              onClick={() => setMostrarFormulario(true)}
            >
              + Agregar Primera Partida
            </button>
          </div>
        ) : (
          <>
            <table className="partidas-tabla">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Descripción</th>
                  <th>Unidad</th>
                  <th className="text-right">Cantidad</th>
                  <th className="text-right">P. Unitario</th>
                  <th className="text-right">Monto Total</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {partidas.map((partida) => (
                  <tr key={partida.id}>
                    {editando && editando.id === partida.id ? (
                      <>
                        <td>
                          <input
                            type="text"
                            value={editando.codigo}
                            onChange={(e) => setEditando({...editando, codigo: e.target.value})}
                            className="form-control-inline"
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={editando.descripcion}
                            onChange={(e) => setEditando({...editando, descripcion: e.target.value})}
                            className="form-control-inline"
                          />
                        </td>
                        <td>
                          <select
                            value={editando.unidad}
                            onChange={(e) => setEditando({...editando, unidad: e.target.value})}
                            className="form-control-inline"
                          >
                            <option value="glb">glb</option>
                            <option value="m²">m²</option>
                            <option value="m³">m³</option>
                            <option value="ml">ml</option>
                            <option value="un">un</option>
                            <option value="kg">kg</option>
                            <option value="ton">ton</option>
                            <option value="%">%</option>
                          </select>
                        </td>
                        <td className="text-right">
                          <input
                            type="number"
                            value={editando.cantidad}
                            onChange={(e) => setEditando({...editando, cantidad: e.target.value})}
                            min="0"
                            step="0.01"
                            className="form-control-inline"
                          />
                        </td>
                        <td className="text-right">
                          <input
                            type="number"
                            value={editando.precio_unitario}
                            onChange={(e) => setEditando({...editando, precio_unitario: e.target.value})}
                            min="0"
                            step="0.01"
                            className="form-control-inline"
                          />
                        </td>
                        <td className="text-right font-bold">
                          {formatearMonto(calcularMonto(
                            editando.cantidad, 
                            editando.precio_unitario, 
                            editando.unidad || partida.unidad
                          ))}
                        </td>
                        <td>
                          <div className="btn-group">
                            <button
                              onClick={() => guardarEdicion(partida.id)}
                              className="btn-icon success"
                              title="Guardar"
                              disabled={guardando}
                            >
                              ✓
                            </button>
                            <button
                              onClick={cancelarEdicion}
                              className="btn-icon danger"
                              title="Cancelar"
                            >
                              ✖
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td>{partida.codigo || '-'}</td>
                        <td>{partida.descripcion}</td>
                        <td>{partida.unidad || '-'}</td>
                        <td className="text-right">{parseFloat(partida.cantidad).toFixed(2)}</td>
                        <td className="text-right">{formatearMonto(partida.precio_unitario)}</td>
                        <td className="text-right font-bold">
                          {formatearMonto(
                            partida.monto_total && partida.monto_total > 0 
                              ? partida.monto_total 
                              : parseFloat(partida.cantidad || 0) * parseFloat(partida.precio_unitario || 0)
                          )}
                        </td>
                        <td>
                          <div className="btn-group">
                            <button
                              onClick={() => iniciarEdicion(partida)}
                              className="btn-icon"
                              title="Editar"
                            >
                              ✏️
                            </button>
                            <button
                              onClick={() => eliminarPartida(partida.id)}
                              className="btn-icon danger"
                              title="Eliminar"
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="total-row">
                  <td colSpan="5" className="text-right font-bold">
                    TOTAL PRESUPUESTO:
                  </td>
                  <td className="text-right font-bold total-amount">
                    {formatearMonto(calcularTotalPresupuesto())}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </>
        )}
      </div>
    </div>
  );
};

export default PresupuestoDetallado;